/**
 * FND-04 · Workspace Header
 *
 * Header pagina "subject-centric": H1 nomina l'entità (persona/azienda/report),
 * con eyebrow di modulo e opzionale navigator prev/next per scorrere entità
 * dello stesso tipo (dipendente successivo/precedente, azienda, report, ecc.).
 *
 * Riutilizzabile: 100% dei moduli (10/10). Sprint V3.0B.
 *
 * Props:
 *   eyebrow    - stringa breve (es. "GI Presenze · Cartellino")
 *   title      - React node (H1 principale)
 *   subtitle   - React node facoltativo (riga sotto il titolo)
 *   onPrev     - handler navigator precedente (facoltativo)
 *   onNext     - handler navigator successivo (facoltativo)
 *   prevLabel  - etichetta bottone precedente
 *   nextLabel  - etichetta bottone successivo
 *   right      - React node ancorato a destra (es. Mode Switcher)
 *   testid     - data-testid del wrapper
 */
import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function WorkspaceHeader({
  eyebrow,
  title,
  subtitle,
  onPrev,
  onNext,
  prevLabel = "Precedente",
  nextLabel = "Successivo",
  right,
  testid = "workspace-header",
}) {
  return (
    <section
      className="relative z-50 px-4 sm:px-6 lg:px-8 pt-4 pb-3 bg-white border-b border-suite-neutral-200"
      data-testid={testid}
    >
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <div
              className="text-[10.5px] font-bold tracking-[0.16em] uppercase text-suite-primary"
              style={{ fontFamily: "Manrope" }}
              data-testid={`${testid}-eyebrow`}
            >
              {eyebrow}
            </div>
          )}
          <h1
            className="text-[20px] sm:text-[22px] lg:text-[26px] font-bold tracking-tight text-suite-neutral-800 leading-[1.2] mt-1 break-words [overflow-wrap:anywhere] hyphens-auto"
            style={{ fontFamily: "Manrope" }}
            data-testid={`${testid}-title`}
          >
            {title}
          </h1>
          {(subtitle || onPrev || onNext) && (
            <div className="mt-1.5 text-[12.5px] text-suite-neutral-500 flex items-center gap-2 flex-wrap">
              {onPrev && (
                <button
                  type="button"
                  onClick={onPrev}
                  className="inline-flex items-center gap-0.5 hover:text-suite-primary transition-colors font-medium"
                  data-testid={`${testid}-nav-prev`}
                >
                  <ChevronLeft size={13} /> {prevLabel}
                </button>
              )}
              {onNext && (
                <>
                  {onPrev && <span className="text-suite-neutral-300">·</span>}
                  <button
                    type="button"
                    onClick={onNext}
                    className="inline-flex items-center gap-0.5 hover:text-suite-primary transition-colors font-medium"
                    data-testid={`${testid}-nav-next`}
                  >
                    {nextLabel} <ChevronRight size={13} />
                  </button>
                </>
              )}
              {subtitle && (
                <>
                  {(onPrev || onNext) && <span className="text-suite-neutral-300">·</span>}
                  <span>{subtitle}</span>
                </>
              )}
            </div>
          )}
        </div>
        {right && <div className="shrink-0">{right}</div>}
      </div>
    </section>
  );
}
